import React from "react"
import { trpc } from "@sens/web/lib/trpc"
import { LogosWithLinks } from "../elements/logosWithLinks"
import { FieldValues, useForm } from "react-hook-form"
import { getPossibleFieldsForListItemSubmission } from "@sens/web/lib/domain/list/addItem"
import { ItemInput } from "./item/input"
import { FormItemState } from "../manage/list/edit"
import { IconField } from "@sens/web/component/elements/iconRenderField"
import { ListWithItemsPublic } from "@sens/web/lib/domain/list/list"
import { ListFieldValueType } from "@sens/core/type/list"

interface AddItemFormProps {
  list: ListWithItemsPublic
  showFooterLogo?: boolean
}

interface FormState extends FieldValues {
  fieldValues: Record<string, ListFieldValueType>

  items: FormItemState[]
  submitterEmail?: string
}

// public-facing form for people to submit a new item to a list
// items will be added to the list as not visible
export const PublicAddItemForm: React.FC<AddItemFormProps> = ({ list, showFooterLogo }) => {
  const { register, handleSubmit, control, formState, watch } = useForm<FormState>({
    defaultValues: {
      items: list.items,
    },
  })
  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const submitItem = trpc.submitItemToList.useMutation()

  const onSubmit = React.useCallback(
    async (formState: FormState) => {
      const result = await submitItem.mutateAsync({
        listId: list.id,
        fields: formState.fieldValues,
        submitterEmail: formState.submitterEmail,
      })

      // after submission
      if (result) {
        setIsSubmitted(true)
      }
    },
    [list.id, submitItem]
  )

  // fetch the user profile
  const userId = list.userId
  const profileQuery = trpc.getProfilePublic.useQuery({ id: userId })

  const shouldShowFooter = React.useMemo(
    () => showFooterLogo && !profileQuery.data?.hideAppBranding && !profileQuery.isInitialLoading,
    [showFooterLogo, profileQuery.data?.hideAppBranding, profileQuery.isInitialLoading]
  )

  // what fields on the list are visible for submission?
  const submissionFields = getPossibleFieldsForListItemSubmission(list)

  return (
    <div className="flex justify-center items-center py-4 px-4 min-h-full sm:px-6 lg:px-8">
      <div className="space-y-8 w-full max-w-md">
        <h3 className="mt-6 mb-6 text-lg font-medium text-slate-800 text-center">
          Suggest a new item for "{list.name}"
        </h3>
        <div className="flex flex-col p-6 bg-gray-50 shadow-sm card">
          {/* error message */}
          {submitItem.error && (
            <div className="flex flex-col justify-center items-center p-4">
              <div className="text-xl font-medium text-slate-800">Error</div>
              <div className="text-lg font-medium text-slate-800">{submitItem.error.message}</div>
            </div>
          )}

          {/* thank you message */}
          {isSubmitted ? (
            <div className="justify-center items-center p-4">
              <div className="text-xl font-medium text-slate-800">
                Thank you for your submission! 🎉
                <br />
                It will be reviewed shortly.
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-2">
              {submissionFields.map((field) => (
                <div key={field.name} className="w-full">
                  <label className="flex gap-1 justify-start label" htmlFor={field.name}>
                    <IconField fieldDefinition={field} className="w-5 h-5 text-gray-500 mt-[-2px]" />
                    <span className="text-lg label-text">{field.name}</span>
                  </label>
                  {/* render a different input based on the field type */}
                  {
                    <ItemInput<FormState>
                      fieldDefinition={field}
                      control={control}
                      register={register}
                      watch={watch as any}
                    />
                  }
                </div>
              ))}

              <div className="mt-2 form-control">
                <label className="label">
                  <span className="after:content-['*'] after:ml-0.5 after:text-red-500 text-lg label-text">
                    Your email?
                  </span>
                </label>
                <input
                  type="email"
                  autoComplete="email"
                  required
                  placeholder="Your email address"
                  className="w-full input input-bordered input-tw"
                  {...register("submitterEmail")}
                />
                <label className="label">
                  <span className="text-gray-600 label-text-alt">
                    Leave your email if you'd like to get an update when your item is posted.
                  </span>
                </label>
              </div>

              <div className="mt-4 text-right">
                <button className="btn btn-primary btn-sm" disabled={submitItem.isPending || !formState.isValid}>
                  Submit
                </button>
              </div>
            </form>
          )}
        </div>
        {/* show or not sens logo */}
        {shouldShowFooter && (
          <div className="pt-2 pr-4 pb-2 pl-4">
            <LogosWithLinks />
          </div>
        )}
      </div>
    </div>
  )
}
